import { useEffect, useState } from 'react';
import { Plus, Pencil, Power, Camera, Wrench } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable, type DataTableColumn } from '../../components/TwDataTable';
import { useServerSort } from '../../hooks/useServerSort';
import {
  PageHeader, Card, Button, IconButton, Modal, Form, useForm, Input, InputNumber, Select,
  Switch, Row, Col, Tag, Tooltip, Alert, Popconfirm, Space, message,
} from '../../ui';

/**
 * COMPLAINT CATEGORIES — the evidence rules, in the hands of the people who set policy.
 *
 * The business rule is "the raiser must attach evidence depending on what the complaint is about".
 * Which categories need what is a commercial decision that changes (a new product line, a spate of
 * transit damage, a distributor arguing about proof), so it is data here rather than a constant that
 * needs a developer and a deploy to move.
 *
 * Each row also owns its SLA and its default severity, which is why a quality failure gets 24 hours
 * and a general query gets 72 without anyone hard-coding a severity ladder.
 */
const SEVERITIES = [
  { value: 'LOW', label: 'Low · 72h default' },
  { value: 'MED', label: 'Medium · 48h default' },
  { value: 'HIGH', label: 'High · 24h default' },
  { value: 'CRIT', label: 'Critical · 4h default' },
];

export function ComplaintCategoriesPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const canManage = usePermission('COMPLAINT.CATEGORY.MANAGE');
  const qc = useQueryClient();

  // Sorting is the backend's ORDER BY (see useServerSort) so every caller of this endpoint sees
  // the same order, and the sort survives a reload or a Back out of a row — it lives in the URL.
  const { sortState, onSortChange, sort } = useServerSort();

  const { data, isLoading } = useQuery({
    queryKey: ['complaint-categories', 'all', sort],
    // Retired categories are shown here (greyed) but nowhere else — an admin needs to see what was
    // switched off, and a raiser must not be able to pick it.
    queryFn: () => request<any[]>({ url: '/complaint-categories', params: { includeInactive: 'true', ...(sort ? { sort } : {}) } }),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => request({ url: `/complaint-categories/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Category retired');
      qc.invalidateQueries({ queryKey: ['complaint-categories'] });
    },
    onError: (e: any) => message.error(
      e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Could not retire that category'),
  });

  const columns: DataTableColumn<any>[] = [
    {
      key: 'name', title: 'Category', dataIndex: 'name', sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => (
        <div>
          <b style={{ opacity: r.active ? 1 : 0.5 }}>{v}</b>
          {!r.active && <Tag tone="neutral" className="ml-2">Retired</Tag>}
          {r.description && <div className="text-xs text-muted">{r.description}</div>}
        </div>
      ),
    },
    { key: 'code', title: 'Code', dataIndex: 'code', sortable: true, render: (v: string) => <span className="text-xs text-muted">{v}</span> },
    {
      key: 'evidence', title: 'Evidence required', sortable: false,
      render: (_: any, r: any) => (r.requiresAttachment ? (
        <Tooltip label={r.attachmentHint ?? 'No hint set — the raiser is told only that a file is needed.'}>
          <Tag tone="gold">
            <Camera size={16} /> {Math.max(1, r.minAttachments || 1)} file{Math.max(1, r.minAttachments || 1) === 1 ? '' : 's'}
          </Tag>
        </Tooltip>
      ) : <span className="text-muted">Optional</span>),
    },
    {
      key: 'needs', title: 'Also requires', sortable: false,
      render: (_: any, r: any) => {
        const bits = [r.requiresProduct && 'Product', r.requiresBatch && 'Batch no'].filter(Boolean);
        return bits.length ? <>{bits.map((b) => <Tag key={String(b)}>{b}</Tag>)}</> : <span className="text-muted">—</span>;
      },
    },
    { key: 'defaultSeverity', title: 'Default urgency', dataIndex: 'defaultSeverity', sortable: true,
      render: (v: string) => <Tag tone={v === 'CRIT' ? 'red' : v === 'HIGH' ? 'gold' : v === 'MED' ? 'gold' : 'blue'}>{v}</Tag> },
    { key: 'slaHours', title: 'SLA', dataIndex: 'slaHours', sortable: true, align: 'right',
      render: (v: number | null) => (v ? `${v} h` : <span className="text-muted">by urgency</span>) },
    ...(canManage ? [{
      key: 'actions', title: '', alwaysVisible: true, width: 92,
      render: (_: any, r: any) => (
        <div className="flex items-center gap-1" onClick={(e: any) => e.stopPropagation()}>
          <Tooltip label="Edit rules">
            <IconButton diameter={32} aria-label={`Edit ${r.name}`}
              className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
              onClick={() => { setEditing(r); setOpen(true); }}>
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
          {r.active && (
            <Popconfirm
              title={`Retire ${r.name}?`}
              description="It disappears from the picker for new complaints. Existing complaints keep it. Refused while open complaints still use it."
              onConfirm={() => deactivate.mutate(r.id)}
            >
              <Tooltip label="Retire">
                <IconButton diameter={32} aria-label={`Retire ${r.name}`}
                  className="border border-line text-muted hover:border-danger-300 hover:bg-danger-50 hover:text-danger-700">
                  <Power size={16} />
                </IconButton>
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    } as DataTableColumn<any>] : []),
  ];

  return (
    <div>
      <PageHeader
        title="Complaint categories"
        subtitle="What each kind of complaint must arrive with, how urgent it is by default, and how long we have to answer it."
        icon={<Wrench />}
        actions={canManage ? (
          <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setOpen(true); }}>
            Add category
          </Button>
        ) : undefined}
      />

      <Alert
        className="mb-4"
        type="info"
        title="These rules are enforced on the server"
        description="A category marked as needing evidence cannot be used to raise a complaint without it — from the web app, the mobile app or the API. Raising the minimum on an existing category only affects NEW complaints; ones already raised are untouched."
      />

      <AutomationSettingsCard />

      <Card bodyClassName="p-0 sm:p-0">
        <div className="p-4">
          <TwDataTable
            storageKey="complaint-categories"
            rowKey="id"
            loading={isLoading}
            dataSource={data ?? []}
            columns={columns}
            sortState={sortState}
            onSortChange={onSortChange}
            onRow={(r) => ({ onClick: canManage ? () => { setEditing(r); setOpen(true); } : undefined })}
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </div>
      </Card>

      <CategoryFormModal open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} />
    </div>
  );
}

/**
 * The automation windows, edited where the rest of the complaint configuration lives.
 *
 * Deliberately NOT a new route or a new permission: `COMPLAINT.CATEGORY.MANAGE` already means
 * "configure how complaints work" and is granted to exactly the right audience. A separate Settings
 * page would be a second place to look and a second thing to forget to grant.
 */
function AutomationSettingsCard() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<any>(null);

  const { data } = useQuery({
    queryKey: ['complaint-settings'],
    queryFn: () => request<any>({ url: '/complaint-settings' }),
  });
  const v = draft ?? data;

  const save = useConfirmedMutation({ title: "Save these automation settings?", content: "They change how new complaints are routed and escalated.", okText: "Yes, save" }, {
    mutationFn: (body: any) => request({ url: '/complaint-settings', method: 'PATCH', data: body }),
    onSuccess: () => {
      message.success('Automation settings saved');
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['complaint-settings'] });
    },
    onError: (e: any) => message.error(
      e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Could not save'),
  });

  if (!v) return null;
  const set = (patch: any) => setDraft({ ...v, ...patch });
  const dirty = !!draft;

  return (
    <Card
      className="mb-4"
      title="Automation"
      extra={dirty ? (
        <Space wrap size={6}>
          <Button size="sm" variant="default" onClick={() => setDraft(null)}>Cancel</Button>
          <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate({
            awaitingInfoTimeoutDays: Number(v.awaitingInfoTimeoutDays),
            resolvedAutoCloseDays: Number(v.resolvedAutoCloseDays),
            awaitingReminderDays: v.awaitingReminderDays,
            autoCancelEnabled: !!v.autoCancelEnabled,
            autoCloseEnabled: !!v.autoCloseEnabled,
            autoEscalateEnabled: !!v.autoEscalateEnabled,
          })}>Save</Button>
        </Space>
      ) : <span className="text-xs text-muted">What happens when nobody replies</span>}
    >
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <div className="type-label mb-1 text-muted">Close an unanswered request after</div>
          <Space wrap size={6}>
            <InputNumber min={1} max={365} value={v.awaitingInfoTimeoutDays}
              onChange={(n: any) => set({ awaitingInfoTimeoutDays: n })} style={{ width: 90 }} />
            <span className="type-body text-muted">days</span>
            <Switch checked={!!v.autoCancelEnabled} onCheckedChange={(c) => set({ autoCancelEnabled: c })} />
          </Space>
          <div className="type-caption mt-1 text-subtle">
            {v.autoCancelEnabled
              ? 'A complaint sent back for evidence is closed if it never arrives.'
              : 'Off — sent-back complaints stay open indefinitely.'}
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div className="type-label mb-1 text-muted">Close a resolved complaint after</div>
          <Space wrap size={6}>
            <InputNumber min={1} max={365} value={v.resolvedAutoCloseDays}
              onChange={(n: any) => set({ resolvedAutoCloseDays: n })} style={{ width: 90 }} />
            <span className="type-body text-muted">days</span>
            <Switch checked={!!v.autoCloseEnabled} onCheckedChange={(c) => set({ autoCloseEnabled: c })} />
          </Space>
          <div className="type-caption mt-1 text-subtle">
            {v.autoCloseEnabled
              ? 'If the complainant neither accepts nor disputes the fix.'
              : 'Off — resolved complaints wait for a person to close them.'}
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div className="type-label mb-1 text-muted">Remind the complainant on day</div>
          <Input
            value={(v.awaitingReminderDays ?? []).join(', ')}
            onChange={(e) => set({
              awaitingReminderDays: e.target.value.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0),
            })}
            placeholder="e.g. 3, 7"
          />
          <div className="type-caption mt-1 text-subtle">
            Comma-separated, counted from the send-back. Each must fall before the close window above.
          </div>
        </Col>
      </Row>
      <div className="mt-3 border-t border-line pt-3">
        <Space size={6}>
          <Switch checked={!!v.autoEscalateEnabled} onCheckedChange={(c) => set({ autoEscalateEnabled: c })} />
          <span className="type-body">Escalate automatically when a complaint passes its SLA</span>
        </Space>
      </div>
    </Card>
  );
}

function CategoryFormModal({ open, record, onClose }: { open: boolean; record: any; onClose: () => void }) {
  const [form] = useForm();
  const qc = useQueryClient();
  const isEdit = !!record;
  // Mirrors the server's own consistency rule: "needs evidence" and "0 files" cannot both be true.
  const [requiresAttachment, setRequiresAttachment] = useState(false);

  useEffect(() => {
    if (open) setRequiresAttachment(!!record?.requiresAttachment);
  }, [open, record]);

  const save = useConfirmedMutation({ title: "Save this complaint category?", content: "It drives where new complaints of this kind are routed.", okText: "Yes, save" }, {
    mutationFn: (vals: any) => request({
      url: isEdit ? `/complaint-categories/${record.id}` : '/complaint-categories',
      method: isEdit ? 'PATCH' : 'POST',
      data: {
        ...(isEdit ? {} : { code: vals.code }),
        name: vals.name,
        description: vals.description || null,
        requiresAttachment,
        minAttachments: requiresAttachment ? Math.max(1, Number(vals.minAttachments ?? 1)) : 0,
        attachmentHint: requiresAttachment ? (vals.attachmentHint || null) : null,
        requiresProduct: !!vals.requiresProduct,
        requiresBatch: !!vals.requiresBatch,
        defaultSeverity: vals.defaultSeverity,
        slaHours: vals.slaHours === '' || vals.slaHours == null ? null : Number(vals.slaHours),
        sortOrder: Number(vals.sortOrder ?? 0),
        ...(isEdit ? { active: !!vals.active } : {}),
      },
    }),
    onSuccess: () => {
      message.success(isEdit ? 'Category updated' : 'Category added');
      qc.invalidateQueries({ queryKey: ['complaint-categories'] });
      onClose();
    },
    onError: (e: any) => message.error(
      e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Could not save'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit ${record?.name}` : 'Add a complaint category'}
      width={640}
    >
      {/*
        * `key` + `initialValues` seeds the form — NOT setFieldsValue in an effect, which races the
        * Radix remount and lands on a form that has already been torn down.
        */}
      <Form
        key={record?.id ?? 'new'}
        form={form}
        layout="vertical"
        onFinish={(v) => save.mutate(v)}
        initialValues={{
          code: record?.code ?? '',
          name: record?.name ?? '',
          description: record?.description ?? '',
          minAttachments: record?.minAttachments ?? 1,
          attachmentHint: record?.attachmentHint ?? '',
          requiresProduct: !!record?.requiresProduct,
          requiresBatch: !!record?.requiresBatch,
          defaultSeverity: record?.defaultSeverity ?? 'MED',
          slaHours: record?.slaHours ?? '',
          sortOrder: record?.sortOrder ?? 0,
          active: record ? !!record.active : true,
        }}
      >
        <Row gutter={16}>
          {!isEdit && (
            <Col xs={24} sm={10}>
              <Form.Item name="code" label="Code" rules={[{ required: true, message: 'A code is required' }]}>
                <Input placeholder="e.g. TRANSIT_DAMAGE" />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={isEdit ? 24 : 14}>
            <Form.Item name="name" label="Name shown to the raiser" rules={[{ required: true, message: 'A name is required' }]}>
              <Input placeholder="e.g. Transit Damage" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="description" label="What this covers (optional)">
          <Input placeholder="One line, shown under the picker so people choose the right one" />
        </Form.Item>

        <Divider label="Evidence" />

        <Form.Item label="Must the raiser attach evidence?">
          <Switch checked={requiresAttachment} onCheckedChange={setRequiresAttachment} />
          <span className="ml-2 text-xs text-muted">
            {requiresAttachment
              ? 'A complaint in this category cannot be submitted without it.'
              : 'Attachments are welcome but optional.'}
          </span>
        </Form.Item>

        {requiresAttachment && (
          <>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item name="minAttachments" label="How many files">
                  <InputNumber min={1} max={10} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={16}>
                <Form.Item
                  name="attachmentHint"
                  label="Tell them exactly what to send"
                  rules={[{ required: true, message: 'Say which photo or document is needed' }]}
                >
                  <Input placeholder="e.g. Photo of the damaged pack as received, before it is opened." />
                </Form.Item>
              </Col>
            </Row>
            <div className="-mt-2 mb-3 text-xs text-muted">
              This sentence is what the raiser reads, and what we quote back if we refuse the complaint.
              &ldquo;Attach a photo&rdquo; gets a photo of nothing useful.
            </div>
          </>
        )}

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="requiresProduct" label="Product must be named" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="requiresBatch" label="Batch number must be given" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Divider label="Urgency and SLA" />

        <Row gutter={16}>
          <Col xs={24} sm={10}>
            <Form.Item name="defaultSeverity" label="Default urgency" rules={[{ required: true }]}>
              <Select options={SEVERITIES} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="slaHours" label="SLA hours">
              <InputNumber min={1} max={720} placeholder="Blank = by urgency" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="sortOrder" label="Order in the picker">
              <InputNumber min={0} max={999} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <div className="-mt-2 mb-3 text-xs text-muted">
          The raiser can pick a HIGHER urgency than the default but never a lower one, so a category
          cannot be used to file an urgent problem as routine and dodge its deadline.
        </div>

        {isEdit && (
          <Form.Item name="active" label="Available for new complaints" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}

        <Button variant="primary" type="submit" block loading={save.isPending}>
          {isEdit ? 'Save rules' : 'Add category'}
        </Button>
      </Form>
    </Modal>
  );
}

/** Small labelled rule to break the form into sections without pulling in a heavier component. */
function Divider({ label }: { label: string }) {
  return (
    <div className="my-4 flex items-center gap-3">
      <span className="type-overline text-muted">{label}</span>
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}
